/**
 * The settings registry: which keys exist, what they hold, and where they live.
 *
 * WHY A REGISTRY AND NOT A FREE-FORM DOCUMENT. `settings_leaf` is a key-value
 * table and would happily store anything, which makes a typo a silent no-op —
 * `/appearence/grouping` writes, reads back, and is never consulted by
 * anything. That is the same failure class as an operator compiling against an
 * empty table, and this repo has spent enough on it. An unknown path is
 * refused, by name.
 *
 * WHY IT LIVES IN `core` AND NOT IN THE ORIGIN. Both ends have to agree about
 * what a key means: the origin validates the write, the client renders the
 * control and holds the default while offline. Two lists would drift, and the
 * drift would be invisible until a value the server accepted was one the client
 * could not display.
 *
 * ## The scope axis, which the settings draft does not have
 *
 * `settings.md` puts palette, theme, density and font scale in the per-account
 * document. `colour.md` says the opposite, and gives a reason that wins: a
 * shared family laptop must not repaint when somebody switches account. Those
 * four are properties of the SCREEN somebody is sitting at, not of the person.
 *
 * So every key declares a `scope`. `account` keys are stored at the origin and
 * follow the person to every device; `device` keys are declared here — so the
 * registry is still the one list of what exists — and REFUSED by the origin
 * with a message saying where they do live. Silently accepting one would make a
 * shared laptop repaint on account switch, months later, for no visible reason.
 */

/** Bumped when a migration changes what a stored document means. See `settings.md` §2. */
export const SETTINGS_SCHEMA_VERSION = 1

export type SettingScope = 'account' | 'device'

export type SettingType = 'boolean' | 'integer' | 'string' | 'enum' | 'json'

export interface SettingSpec {
  /** An RFC 6901 JSON Pointer. `<id>` segments are matched by `collection`. */
  readonly path: string
  readonly scope: SettingScope
  readonly type: SettingType
  /** For `enum`: the values admitted. */
  readonly values?: readonly string[]
  /** For `integer`: inclusive bounds. */
  readonly min?: number
  readonly max?: number
  /** What the value is when nobody has set it. Absent for collections. */
  readonly default?: unknown
  /**
   * This path is a COLLECTION PREFIX: `/writing/signatures` admits
   * `/writing/signatures/<id>`, each holding a whole subtree as one leaf.
   *
   * One leaf per member, deliberately, and `settings.md` gives the reason: two
   * devices editing different signatures must not collide, and they would if
   * the collection were one leaf holding an object.
   */
  readonly collection?: boolean
  /** Why a device-scoped key is not stored at the origin. Shown in the refusal. */
  readonly because?: string
}

/**
 * Every key this build knows.
 *
 * DELIBERATELY NOT THE WHOLE OF `settings.md` §1. A key for a feature that does
 * not exist is a key a client can write and nothing will ever read — the same
 * silent no-op the registry exists to prevent, one level up. Splits, the
 * Screener, snippets, storage budgets and the security screens are absent
 * because their features are; they arrive with them.
 */
export const SETTINGS: readonly SettingSpec[] = [
  // ── Appearance ────────────────────────────────────────────────────────────
  //
  // The four device-scoped ones are declared so that a client asking "is this a
  // real key" gets "yes, and not here" rather than "no such key", which would
  // send somebody looking for a typo that is not there.
  {
    path: '/appearance/palette',
    scope: 'device',
    type: 'string',
    because: 'a shared family laptop must not repaint when somebody switches account (colour.md)',
  },
  {
    path: '/appearance/theme',
    scope: 'device',
    type: 'enum',
    values: ['light', 'dark', 'system'],
    because: 'a shared family laptop must not repaint when somebody switches account (colour.md)',
  },
  {
    path: '/appearance/density',
    scope: 'device',
    type: 'enum',
    values: ['compact', 'cozy', 'comfortable'],
    because: 'row height is a property of the screen you are sitting at, not of the account (colour.md)',
  },
  {
    path: '/appearance/font_scale',
    scope: 'device',
    type: 'integer',
    min: 1,
    max: 7,
    because: 'text size is a property of the screen you are sitting at, not of the account (colour.md)',
  },

  /**
   * THREADS OR MESSAGES, and it is the account's because it is a way of
   * reading rather than a property of the screen. The owner's requirement was
   * that it be SELECTABLE — "some like it this way, some the other" — so it is
   * a setting and not a decision. `decisions.md` fixes the spelling and the
   * default.
   *
   * HONOURED NOW. This leaf spent two steps written and unread, because the
   * search API answered `bad_group` for anything but messages; rung 11's
   * `thread_aggregate` and `Store.searchThreads` are what the API needed, and
   * `?group=` now serves both.
   *
   * THE SPELLING HERE IS PLURAL AND THE API'S IS SINGULAR — `threads` against
   * `?group=thread` — because this vocabulary was fixed by `decisions.md`
   * before the parameter existed and the parameter was written against its own.
   * Neither is worth changing now: this one is already stored on accounts, and
   * that one is already in the API's error text. The client reconciles them in
   * exactly one place (`apps/web/src/settings.ts`), which is the only way two
   * spellings are safe.
   */
  {
    path: '/appearance/grouping',
    scope: 'account',
    type: 'enum',
    values: ['threads', 'messages'],
    default: 'messages',
  },
  {
    path: '/appearance/reading_pane',
    scope: 'account',
    type: 'enum',
    values: ['right', 'below', 'none'],
    default: 'right',
  },

  // ── The Screener ──────────────────────────────────────────────────────────
  //
  // `settings.md` specifies `/screener/enabled` with a default of `true`, and
  // that default is the one thing here that is deliberately NOT what the draft
  // says. `review.md` traced it: the prelude files any sender with no row in
  // `known_senders` into `Hearth/Screener`, nothing in onboarding filled that
  // table, and a real IMAP folder catching 100% of day-one mail empties the
  // Inbox for the family members reading on iOS Mail and Thunderbird — with no
  // explanation and nothing to click. Rung 12 fills the table, and this default
  // means the switch is a thing somebody turns on after being shown what it will
  // do, rather than a thing that has already happened to them.
  //
  // THE DEFAULT IS PINNED BY A TEST, because it is the whole fix and it looks
  // like a preference: nothing about the line says that changing `false` to
  // `true` is the difference between a feature and four empty Inboxes.
  {
    path: '/screener/enabled',
    scope: 'account',
    type: 'boolean',
    default: false,
    because: 'on by default with an empty sender table empties the Inbox on day one (review.md blocker)',
  },
  /**
   * Replying to a screened sender lets them in.
   *
   * IT IS NOT A SECOND MECHANISM. The seed's rule is "everyone on a thread this
   * account sent into", so a reply IS what admits a sender — the same derivation
   * asked again after the next sync. The setting exists so that somebody who
   * wants the Screener to hold its line can say so.
   */
  { path: '/screener/auto_yes_if_replied', scope: 'account', type: 'boolean', default: true },
  {
    path: '/screener/digest',
    scope: 'account',
    type: 'enum',
    values: ['off', 'daily_0800'],
    default: 'daily_0800',
  },

  // ── Reading ───────────────────────────────────────────────────────────────
  {
    path: '/reading/remote_images',
    scope: 'account',
    type: 'enum',
    values: ['never', 'contacts', 'always'],
    default: 'contacts',
  },
  {
    /**
     * `0` = mark read the moment it opens, `-1` = never automatically. Both are
     * real answers people want, which is why this is not a boolean and not a
     * positive integer.
     */
    path: '/reading/mark_read_after_ms',
    scope: 'account',
    type: 'integer',
    min: -1,
    max: 60_000,
    default: 2000,
  },
  {
    path: '/reading/quoted_text',
    scope: 'account',
    type: 'enum',
    values: ['fold', 'show'],
    default: 'fold',
  },
  { path: '/reading/preview_lines', scope: 'account', type: 'integer', min: 0, max: 3, default: 1 },
  {
    path: '/reading/unread_counts',
    scope: 'account',
    type: 'enum',
    values: ['threads', 'messages'],
    default: 'threads',
  },

  // ── Writing ───────────────────────────────────────────────────────────────
  //
  // These exist before the composer does, and that is deliberate rather than
  // premature: `undo_send_s` in particular has to be readable by the ORIGIN,
  // which is the scheduling authority for the outbox, and a value the origin
  // cannot see is a hold it cannot honour.
  {
    path: '/writing/undo_send_s',
    scope: 'account',
    type: 'integer',
    min: 0,
    max: 30,
    default: 10,
  },
  {
    path: '/writing/reply_default',
    scope: 'account',
    type: 'enum',
    values: ['reply', 'reply_all'],
    default: 'reply',
  },
  { path: '/writing/send_and_archive', scope: 'account', type: 'boolean', default: true },
  /** One leaf per signature: two devices editing different ones must not collide. */
  { path: '/writing/signatures', scope: 'account', type: 'json', collection: true },

  // ── Keyboard ──────────────────────────────────────────────────────────────
  {
    path: '/keys/preset',
    scope: 'account',
    type: 'enum',
    values: ['gmail', 'mutt', 'none'],
    default: 'gmail',
  },
  /** `{"chord": "command.id"}`. One leaf, because it is edited as a whole. */
  { path: '/keys/overrides', scope: 'account', type: 'json', default: {} },
]

const BY_PATH = new Map(SETTINGS.map((spec) => [spec.path, spec]))
const COLLECTIONS = SETTINGS.filter((spec) => spec.collection)

/**
 * A ULID, or anything else a collection member is keyed by.
 *
 * Deliberately loose about the FORMAT and strict about the SHAPE: one path
 * segment, no slashes, nothing that could climb out of its collection. A
 * registry that insisted on Crockford base32 would reject a perfectly good id
 * from a client that generates them differently, and gain nothing — the id is
 * an opaque key here.
 */
const MEMBER_ID = /^[A-Za-z0-9_-]{1,64}$/

export interface Resolved {
  readonly spec: SettingSpec
  /** The collection member id, when the path named one. */
  readonly member?: string
}

/** Which spec, if any, admits this path. */
export function resolvePath(path: string): Resolved | null {
  const exact = BY_PATH.get(path)
  if (exact && !exact.collection) return { spec: exact }

  const cut = path.lastIndexOf('/')
  if (cut <= 0) return null
  const prefix = path.slice(0, cut)
  const member = path.slice(cut + 1)
  const collection = COLLECTIONS.find((spec) => spec.path === prefix)
  if (!collection || !MEMBER_ID.test(member)) return null
  return { spec: collection, member }
}

export type Refusal =
  | { readonly code: 'unknown_path'; readonly message: string }
  | { readonly code: 'device_scoped'; readonly message: string }
  | { readonly code: 'bad_value'; readonly message: string }

/**
 * Is this a leaf the ORIGIN may store?
 *
 * Returns `null` when it is, and a typed refusal when it is not. A tombstone
 * (`value === null`) skips the value check: deleting a key does not have to
 * satisfy its type, and a client clearing a value it cannot represent is
 * exactly what a stale client should still be allowed to do.
 */
export function validateLeaf(path: string, value: unknown): Refusal | null {
  const resolved = resolvePath(path)
  if (!resolved) {
    return {
      code: 'unknown_path',
      message: `${path} is not a settings key this build knows. See packages/core/src/settings for the list.`,
    }
  }
  const { spec } = resolved
  if (spec.scope === 'device') {
    return {
      code: 'device_scoped',
      message:
        `${path} is stored per browser profile, not per account — ${spec.because}. ` +
        `Writing it here would make it follow the person to every screen, which is the opposite of the intent.`,
    }
  }
  if (value === null) return null

  switch (spec.type) {
    case 'boolean':
      return typeof value === 'boolean'
        ? null
        : { code: 'bad_value', message: `${path} takes a boolean, got ${typeName(value)}` }
    case 'integer': {
      if (typeof value !== 'number' || !Number.isInteger(value)) {
        return { code: 'bad_value', message: `${path} takes an integer, got ${typeName(value)}` }
      }
      if (spec.min !== undefined && value < spec.min) {
        return { code: 'bad_value', message: `${path} must be at least ${spec.min}, got ${value}` }
      }
      if (spec.max !== undefined && value > spec.max) {
        return { code: 'bad_value', message: `${path} must be at most ${spec.max}, got ${value}` }
      }
      return null
    }
    case 'string':
      return typeof value === 'string'
        ? null
        : { code: 'bad_value', message: `${path} takes a string, got ${typeName(value)}` }
    case 'enum':
      return typeof value === 'string' && spec.values?.includes(value)
        ? null
        : {
            code: 'bad_value',
            message: `${path} takes one of ${spec.values?.join(', ')}, got ${JSON.stringify(value)}`,
          }
    case 'json':
      // An object or an array. Deliberately not "anything JSON can hold": a bare
      // scalar under a `json` key is almost always a client that meant to write
      // one level deeper, and accepting it would store a shape nothing reads.
      return typeof value === 'object'
        ? null
        : { code: 'bad_value', message: `${path} takes an object, got ${typeName(value)}` }
  }
}

const typeName = (value: unknown): string =>
  value === null ? 'null' : Array.isArray(value) ? 'array' : typeof value

/** The document as it is when nobody has set anything. Account-scoped keys only. */
export function defaults(): Record<string, unknown> {
  const doc: Record<string, unknown> = {}
  for (const spec of SETTINGS) {
    if (spec.scope !== 'account' || spec.default === undefined) continue
    setPointer(doc, spec.path, spec.default)
  }
  return doc
}

/**
 * Build the nested document from stored leaves, over the defaults.
 *
 * DEFAULTS ARE MERGED HERE, at the origin, rather than left to the client. Both
 * ends would otherwise hold a copy of every default and a disagreement would be
 * invisible: the server thinks `preview_lines` is 1, the client renders 2, and
 * nothing is wrong anywhere you can point at. The client still ships the
 * registry — it needs it to render controls, and to read settings offline —
 * but the document it displays comes from one place.
 */
export function project(leaves: readonly { path: string; value: unknown }[]): Record<string, unknown> {
  const doc = defaults()
  for (const leaf of leaves) {
    // A tombstone restores the default rather than leaving a hole, which is
    // what "unset it" means to the person who cleared the box.
    if (leaf.value === null) continue
    setPointer(doc, leaf.path, leaf.value)
  }
  return doc
}

/** Write `value` at an RFC 6901 pointer, creating the objects on the way. */
export function setPointer(doc: Record<string, unknown>, pointer: string, value: unknown): void {
  const parts = pointer.split('/').slice(1).map(unescapePointer)
  let node = doc
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]!
    const next = node[key]
    if (typeof next !== 'object' || next === null || Array.isArray(next)) node[key] = {}
    node = node[key] as Record<string, unknown>
  }
  node[parts[parts.length - 1]!] = value
}

/** RFC 6901: `~1` is `/` and `~0` is `~`, in that order. */
const unescapePointer = (part: string): string => part.replace(/~1/g, '/').replace(/~0/g, '~')

/**
 * `"v<schema>.<lamport>"` — the etag `settings.md` §2 specifies.
 *
 * The schema is in it because a document that means something different is a
 * different document, whatever its lamport says.
 */
export const settingsEtag = (lamport: number): string => `"v${SETTINGS_SCHEMA_VERSION}.${lamport}"`
